import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import { ArrowRight, Check, ChevronDown, Loader2 } from "lucide-react";

type AcceptButtonProps = {
  acceptPreference: "accept" | "acceptAndSwitch";
  isAccepting: boolean;
  disabled: boolean;
  isDropdownOpen: boolean;
  projectName: string;
  onDefaultAction: () => void;
  onAccept: () => void;
  onAcceptAndSwitch: () => void;
  onDropdownOpenChange: (open: boolean) => void;
};

export function AcceptButton({
  acceptPreference,
  isAccepting,
  disabled,
  isDropdownOpen,
  projectName,
  onDefaultAction,
  onAccept,
  onAcceptAndSwitch,
  onDropdownOpenChange,
}: AcceptButtonProps) {
  const getButtonIcon = () => {
    if (isAccepting) {
      return <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
    }
    if (acceptPreference === "acceptAndSwitch") {
      return <ArrowRight className="mr-2 h-4 w-4" />;
    }
    return <Check className="mr-2 h-4 w-4" />;
  };

  const getButtonText = () => {
    if (isAccepting) {
      return "Accepting...";
    }
    if (acceptPreference === "acceptAndSwitch") {
      return "Accept & Switch";
    }
    return "Accept";
  };

  return (
    <div className="relative flex">
      <Button
        className="flex-1 rounded-r-none bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
        disabled={disabled}
        onClick={onDefaultAction}
      >
        {getButtonIcon()}
        {getButtonText()}
      </Button>
      <DropdownMenu onOpenChange={onDropdownOpenChange} open={isDropdownOpen}>
        <DropdownMenuTrigger
          render={
            <Button
              className="rounded-l-none border-green-700/50 border-l bg-green-600 px-2 hover:bg-green-700 dark:border-green-800/50 dark:bg-green-700 dark:hover:bg-green-800"
              disabled={disabled}
            />
          }
        >
          <ChevronDown className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-3 py-2.5"
            disabled={disabled}
            onClick={onAcceptAndSwitch}
          >
            <ArrowRight className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">Accept and switch</span>
              <span className="text-muted-foreground text-xs">
                Switch to {projectName}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-3 py-2.5"
            disabled={disabled}
            onClick={onAccept}
          >
            <Check className="h-4 w-4 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">Accept only</span>
              <span className="text-muted-foreground text-xs">
                Stay on current project
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
